import {
  addMonths,
  endOfMonth,
  format,
  isAfter,
  isBefore,
  parseISO,
  startOfDay,
  startOfMonth,
} from 'date-fns';

/**
 * Generate every monthly period key (YYYY-MM) between dateFrom and dateTo inclusive.
 */
export function generateMonthlyPeriodKeys(dateFrom: string, dateTo: string): string[] {
  const periods: string[] = [];
  let current = startOfMonth(parseISO(dateFrom));
  const end = startOfMonth(parseISO(dateTo));

  while (!isAfter(current, end)) {
    periods.push(format(current, 'yyyy-MM'));
    current = addMonths(current, 1);
  }

  return periods;
}

/**
 * Monthly periods to render for the chart.
 * Historical ranges include every month; active ranges stop at the current month.
 */
export function getMonthlyChartPeriodKeys(
  dateFrom: string,
  dateTo: string,
  now: Date = new Date()
): string[] {
  const rangeEnd = parseISO(dateTo);
  const today = startOfDay(now);

  if (isBefore(rangeEnd, today)) {
    return generateMonthlyPeriodKeys(dateFrom, dateTo);
  }

  return generateMonthlyPeriodKeys(dateFrom, format(now, 'yyyy-MM-dd'));
}

/**
 * Average monthly spend per category from completed periods with non-zero totals.
 */
export function computeCategoryMonthlyAverages(
  periodMap: Map<string, Record<string, number>>,
  periodKeys: string[],
  categoryKeys: string[]
): Map<string, number> {
  const averages = new Map<string, number>();

  for (const categoryKey of categoryKeys) {
    const amounts = periodKeys
      .map((period) => periodMap.get(period)?.[categoryKey] ?? 0)
      .filter((amount) => amount > 0);

    averages.set(
      categoryKey,
      amounts.length > 0 ? amounts.reduce((sum, value) => sum + value, 0) / amounts.length : 0
    );
  }

  return averages;
}

/**
 * Whether to append a synthetic projected month after the last actual period.
 * Projections are only shown for forward-looking ranges that extend into the future.
 */
export function shouldAppendProjectedMonth(
  nextPeriodKey: string,
  dateTo: string,
  now: Date = new Date()
): boolean {
  const rangeEnd = parseISO(dateTo);
  const nextPeriodStart = startOfMonth(parseISO(`${nextPeriodKey}-01`));
  const today = startOfDay(now);

  // Never project beyond the selected range end.
  if (isAfter(nextPeriodStart, rangeEnd)) {
    return false;
  }

  // Historical ranges should only show actual data.
  if (isBefore(rangeEnd, today)) {
    return false;
  }

  // Only project the single month immediately after the current month.
  const projectedMonth = addMonths(startOfMonth(now), 1);
  return nextPeriodStart.getTime() === projectedMonth.getTime();
}

/**
 * Whether an incomplete current month should be marked as projected.
 */
export function shouldMarkPeriodAsProjected(
  periodKey: string,
  granularity: 'daily' | 'weekly' | 'monthly',
  now: Date = new Date()
): boolean {
  if (granularity !== 'monthly') {
    return false;
  }

  const currentMonth = format(startOfMonth(now), 'yyyy-MM');
  if (periodKey !== currentMonth) {
    return false;
  }

  return isBefore(now, endOfMonth(now));
}
